import { ChangeEvent, KeyboardEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getArtistGroups } from '@/apis/main';
import { ArtistGroup } from '@/types/photocard';
import { PhotocardsContext } from '@/routes/photocard/_components/Provider';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Image } from '@/components/Image';
import { WebviewActionHideKeyboard } from '@/utils/action';
import { useIntl } from 'react-intl';
import { trackEvent } from '../Event/_lib/eventTrack';

const ALPHABET = [
  '🔥',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'R',
  'S',
  'T',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  '#'
];

const STICKY_HEADER_PX = 32;
const CLICK_SUPPRESS_MS = 1500;

type Section = { key: string; label: string; items: ArtistGroup[] };

export function GroupFilter() {
  const intl = useIntl();
  const { group, setGroup } = useContext(PhotocardsContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const suppressAutoSelectRef = useRef<number>(0);
  const [selectedLetter, setSelectedLetter] = useState<string>('🔥');

  const paramsSearchValue = searchParams.get('searchQ') ?? '';
  const [searchValue, setSearchValue] = useState(paramsSearchValue);

  useEffect(() => {
    setSearchValue(paramsSearchValue);
  }, [paramsSearchValue]);

  const { data: artistGroups } = useQuery({
    queryFn: () => getArtistGroups(paramsSearchValue),
    queryKey: ['group-list', paramsSearchValue],
    select: (data) => data.groups
  });

  const updateSearchParam = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      const trimmed = value.trim();
      if (trimmed) nextParams.set('searchQ', value);
      else nextParams.delete('searchQ');
      const query = nextParams.toString();
      if (query === searchParams.toString()) return;
      navigate(query ? `${location.pathname}?${query}` : location.pathname, { replace: true });
    },
    [location.pathname, navigate, searchParams]
  );

  const handleResetSearch = () => {
    setSearchValue('');
    updateSearchParam('');
    WebviewActionHideKeyboard();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchValue(value);
    updateSearchParam(value);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      WebviewActionHideKeyboard();
      updateSearchParam(event.currentTarget.value);
    }
  };

  const getInitial = (name?: string) => {
    if (!name || name.length === 0) return '#';
    const zh = name.trim().charAt(0).toUpperCase();
    return /[A-Z]/.test(zh) ? zh : '#';
  };

  const { sections, availableLetters, hotGroupIds } = useMemo(() => {
    const groups: ArtistGroup[] = artistGroups ?? [];
    const hot = groups.slice(0, 10);
    const rest = groups.slice(10).sort((a, b) => a.name_en.localeCompare(b.name_en));

    const bucket = new Map<string, ArtistGroup[]>();
    for (const g of rest) {
      const key = getInitial(g.name_en);
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key)!.push(g);
    }

    const result: Section[] = [];
    if (hot.length) result.push({ key: '🔥', label: '🔥 HOT', items: hot });

    for (const zh of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
      const arr = bucket.get(zh);
      if (arr?.length) result.push({ key: zh, label: zh, items: arr });
    }
    if (bucket.get('#')?.length) result.push({ key: '#', label: '#', items: bucket.get('#')! });

    return {
      sections: result,
      availableLetters: result.map((s) => s.key),
      hotGroupIds: new Set(hot.map((g) => g.id))
    };
  }, [artistGroups]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const now = Date.now();
      if (now - suppressAutoSelectRef.current < CLICK_SUPPRESS_MS) {
        return;
      }
      // sticky 헤더 하단 가장자리(= 기준선)
      const guide = container.scrollTop + STICKY_HEADER_PX;

      const offsets = Object.entries(sectionRefs.current)
        .map(([key, el]) => {
          if (!el) return null;
          const top = el.offsetTop - container.offsetTop;
          return { key, top };
        })
        .filter(Boolean)
        .sort((a, b) => a!.top - b!.top) as { key: string; top: number }[];

      // 기준선(guide)보다 위쪽(<=)인 섹션들 중 가장 아래(마지막) 섹션을 현재로 간주
      let currentKey = offsets[0]?.key;
      for (const o of offsets) {
        if (o.top <= guide + 1) currentKey = o.key;
        else break;
      }

      if (currentKey && currentKey !== selectedLetter) {
        setSelectedLetter(currentKey);
      }
    };

    // 초기 1회 계산 + 스크롤 이벤트
    onScroll();
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [sections, selectedLetter]);

  // 클릭 시 알파벳 섹션으로 이동
  const scrollToLetter = (letter: string) => {
    const container = scrollContainerRef.current;
    const target = sectionRefs.current[letter];

    suppressAutoSelectRef.current = Date.now();
    setSelectedLetter(letter);

    if (!container) return;

    if (target) {
      container.scrollTo({
        top: target.offsetTop - container.offsetTop,
        behavior: 'smooth'
      });
    } else {
      // 마지막 섹션예외처리 (Y, Z, #)
      const lastSectionKeys = ['Y', 'Z', '#'];
      if (lastSectionKeys.includes(letter)) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleGroupClick = (artist: ArtistGroup) => {
    setGroup(artist);
    trackEvent('PLP_click_group', { Group: artist });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('group', String(artist.id));
    nextParams.delete('member');
    nextParams.delete('q');
    nextParams.delete('searchQ');
    const query = nextParams.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname);
  };

  return (
    <div className="fixed top-[75px] left-0 h-[calc(100vh-75px)] w-[260px] border-r border-[#f5f5f5] bg-white flex flex-col">
      {/* 검색 바 */}
      <div className="w-full flex gap-2 items-center border-b border-[#f5f5f5] py-4 px-5 h-[62px]">
        <Image src="/icons/search-gray.svg" alt="search-svg" width={24} />
        <input
          className="text-[14px] font-medium w-full outline-none"
          type="text"
          value={searchValue}
          placeholder={intl.formatMessage({ id: 'search_group', defaultMessage: 'Search Group' })}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={() => trackEvent('PLP_click_search_group')}
        />
        {searchValue && (
          <button type="button" aria-label="Clear search" onClick={handleResetSearch} className="p-0 bg-transparent border-0">
            <Image src="/icons/huge.svg" alt="" aria-hidden width={24} />
          </button>
        )}
      </div>

      {/* 좌측 알파벳 인덱스 + 우측 리스트 */}
      <div className="flex flex-1 overflow-hidden">
        {!paramsSearchValue && (
          <div className="flex flex-col items-center gap-1 select-none w-7 pt-5 bg-[#F8F8F8]">
            {ALPHABET.map((letter) => {
              const enabled = availableLetters.includes(letter);
              const isSelected = selectedLetter === letter;

              return (
                <button
                  key={`idx-${letter}`}
                  type="button"
                  disabled={!enabled}
                  className={`w-7 h-6 flex items-center font-medium justify-center text-[10px] transition-colors rounded 
                    ${isSelected ? 'bg-[#FFE5E5] text-[#FF6060]' : 'text-gray-700'} 
                    disabled:text-[#d9d9d9]`}
                  onClick={() => {
                    scrollToLetter(letter);
                    trackEvent('PLP_click_Alphabet', { Click_value: letter });
                  }}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="flex-1 h-full flex flex-col no-scrollbar overflow-y-auto px-4 [overflow-anchor:none] pb-[250px]"
        >
          {sections.map((section) => (
            <div key={section.key} className="w-full" ref={(el) => (sectionRefs.current[section.key] = el)}>
              {!paramsSearchValue && (
                <div className="pl-[10px] my-1 flex items-center sticky top-0 z-10 py-2 bg-gray-50 backdrop-blur">
                  <span className="text-xs font-semibold text-[#262626]">
                    {section.key === '🔥'
                      ? `${intl.formatMessage({ id: 'hot_now', defaultMessage: 'HOT NOW' })} 🔥`
                      : section.label}
                  </span>
                </div>
              )}

              {section.items.map((artist) => (
                <div
                  key={`${section.key}:${artist.id}`}
                  className={`w-full rounded-lg flex justify-start items-center gap-2 px-[10px] py-3 cursor-pointer border 
                    ${artist.name_en === group?.name_en ? 'bg-[#FFF2F2] border-[#FF6060]' : 'border-transparent'}`}
                  onClick={() => handleGroupClick(artist)}
                >
                  <Image src={artist.image} alt="artist logo" className="w-[32px] h-[32px] rounded-full" width={32} height={32} />
                  <span className="text-[14px] leading-5 font-semibold text-center truncate max-w-[180px]">{artist.name_en}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
